import React, { useEffect, useState, useCallback } from "react";
import { Spin, Tooltip, Pagination, message, Modal } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { ArrowLeftRight, Copy, ChevronRight, Layers, Coins, Hash, Calendar, Wallet } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch } from "react-redux";
import paymentApi from "../../api/paymentApi";
import promosApiExports from "../../api/promosApi";
import blockchainApi from "../../api/blockchainApi";
import PromoDrawLayer from "../DrawerLayers/PromoDrawerLayer/PromoDrawLayer";
import { setSelectedPromoID } from "../../store/slices/selectedPromoIDSlice";
import { setPromoForm } from "../../store/slices/promoFormSlice";
import { openFullDrawer } from "../../store/slices/fullDrawerSlice";
import CoinBadge from "../../components/coin_badge/CoinBadge";

const promosApi = promosApiExports.promosApi;

function cn(...c) {
    return c.filter(Boolean).join(" ");
}

const shellClass =
    "relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl shadow-[0_18px_60px_-30px_rgba(0,0,0,0.9)]";

const short = (str, start = 6, end = 4) =>
    str ? `${str.slice(0, start)}…${str.slice(-end)}` : "—";

const copyText = (text) => {
    navigator.clipboard.writeText(text);
    message.success("Скопировано");
};

const PROMO_TYPE_LABEL = {
    cryptoOnly:       { label: "Crypto",          cls: "border-amber-400/30 bg-amber-500/10 text-amber-200" },
    crystalsAndCrypto:{ label: "Crypto+Crystals",  cls: "border-white/15 bg-white/[0.06] text-white/65" },
    onlyCrystals:     { label: "Crystals",          cls: "border-violet-400/30 bg-violet-500/10 text-violet-200" },
};

const STATUS_LABEL = {
    ACTIVE:             { label: "Live",       cls: "border-emerald-400/35 bg-emerald-500/12 text-emerald-200" },
    PENDING:            { label: "Pending",    cls: "border-amber-400/35 bg-amber-500/12 text-amber-200" },
    COMPLETED:          { label: "Completed",  cls: "border-sky-400/35 bg-sky-500/12 text-sky-200" },
    MANUALLY_COMPLETED: { label: "Completed",  cls: "border-sky-400/35 bg-sky-500/12 text-sky-200" },
};

const BC_STATUS_LABEL = {
    funded:          { label: "Funded",       cls: "border-emerald-400/35 bg-emerald-500/12 text-emerald-200" },
    pending_fee:     { label: "Pending fee",  cls: "border-yellow-400/35 bg-yellow-500/12 text-yellow-200" },
    pending_deposit: { label: "Pending dep.", cls: "border-orange-400/35 bg-orange-500/12 text-orange-200" },
    claiming:        { label: "Claiming",     cls: "border-blue-400/35 bg-blue-500/12 text-blue-200" },
    completed:       { label: "Done",         cls: "border-sky-400/35 bg-sky-500/12 text-sky-200" },
    withdrawn:       { label: "Withdrawn",    cls: "border-rose-400/35 bg-rose-500/12 text-rose-200" },
};

function Pill({ label, cls }) {
    if (!label) return <span className="text-white/25">—</span>;
    return (
        <span className={cn("inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide", cls)}>
            {label}
        </span>
    );
}

function HashChip({ value }) {
    if (!value) return <span className="text-white/25 text-[11px]">—</span>;
    return (
        <Tooltip title={value}>
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); copyText(value); }}
                className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/[0.05] px-2 py-0.5 font-mono text-[11px] text-white/60 transition hover:border-white/20 hover:text-white/90"
            >
                {short(value, 8, 6)}
                <Copy size={9} className="shrink-0 opacity-50" />
            </button>
        </Tooltip>
    );
}

function PromoMiniCard({ item, onOpen, loading }) {
    const pt = PROMO_TYPE_LABEL[item.promoType] || { label: item.promoType, cls: "border-white/15 bg-white/[0.06] text-white/55" };
    const st = STATUS_LABEL[item.promoStatus] || { label: item.promoStatus, cls: "border-white/15 bg-white/[0.06] text-white/55" };

    return (
        <motion.button
            type="button"
            whileHover={{ scale: 1.015 }}
            whileTap={{ scale: 0.985 }}
            onClick={() => onOpen(item.promoId)}
            disabled={loading}
            className="group flex w-full min-w-[200px] flex-col gap-1.5 rounded-xl border border-white/10 bg-white/[0.04] p-2.5 text-left transition hover:border-emerald-400/30 hover:bg-white/[0.07] disabled:opacity-60"
        >
            {/* Top row: ID + status */}
            <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-[13px] font-bold text-emerald-300/90">
                    #{item.promoId}
                </span>
                <div className="flex items-center gap-1.5">
                    <Pill label={st.label} cls={st.cls} />
                    <ChevronRight
                        size={12}
                        className="text-white/25 transition group-hover:text-emerald-300/70"
                    />
                </div>
            </div>

            {/* Title */}
            {item.promoTitle && (
                <div className="truncate text-[11px] font-medium text-white/60" title={item.promoTitle}>
                    {item.promoTitle}
                </div>
            )}

            {/* Bottom row: type + amount */}
            <div className="flex items-center justify-between gap-2 pt-0.5">
                <Pill label={pt.label} cls={pt.cls} />
                {item.promoType !== "onlyCrystals" && item.totalAmount != null && (
                    <span className="font-mono text-[11px] text-white/50">
                        {Number(item.totalAmount).toLocaleString()} <span className="text-white/30">{item.promoTokenType || ""}</span>
                    </span>
                )}
            </div>

            {loading && (
                <div className="mt-1 text-[10px] text-white/35">Загрузка...</div>
            )}
        </motion.button>
    );
}

const th = "whitespace-nowrap px-3 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wide text-white/50 sm:px-4";
const td = "px-3 py-3 align-middle text-[12px] text-white/85 sm:px-4";

export default function TransactionsListLayer() {
    const dispatch = useDispatch();

    const [items, setItems]           = useState([]);
    const [total, setTotal]           = useState(0);
    const [page, setPage]             = useState(1);
    const [pageSize]                  = useState(20);
    const [loading, setLoading]       = useState(false);
    const [openingId, setOpeningId]   = useState(null);
    const [bcOnly, setBcOnly]         = useState(false);

    const fetchData = useCallback(async (p = page) => {
        setLoading(true);
        try {
            const res = await paymentApi.getAllWithPromos({ page: p, pageSize });
            const data = res?.data || res;
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch {
            message.error("Не удалось загрузить транзакции");
        } finally {
            setLoading(false);
        }
    }, [page, pageSize]);

    useEffect(() => {
        fetchData(page);
    }, [page]);

    const handleOpenPromo = async (promoId) => {
        setOpeningId(promoId);
        try {
            const promo = await promosApi.getById(promoId);
            dispatch(setSelectedPromoID(promoId));
            dispatch(setPromoForm(promo));
            dispatch(openFullDrawer(<PromoDrawLayer create={false} />));
        } catch {
            message.error("Не удалось загрузить данные промо");
        } finally {
            setOpeningId(null);
        }
    };

    const visibleItems = bcOnly
        ? items.filter(i => i.chainId || i.feeTxHash || i.depositTxHash)
        : items;

    const bcCount = items.filter(i => i.chainId || i.feeTxHash || i.depositTxHash).length;

    return (
        <div className="flex h-full min-h-0 flex-1 flex-col gap-4 overflow-hidden p-4 text-white sm:p-5">
            {/* Header */}
            <section className={cn(shellClass, "shrink-0 p-4 sm:p-5")}>
                <div className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full bg-emerald-500/10 blur-3xl" />
                <div className="relative flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 items-center gap-3">
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06]">
                            <ArrowLeftRight className="h-5 w-5 text-emerald-300/90" strokeWidth={2} />
                        </span>
                        <div>
                            <h1 className="text-lg font-semibold tracking-tight text-white/95 sm:text-xl">
                                Transactions
                            </h1>
                            <p className="text-[12px] text-white/45">
                                Оплаты комиссий и on-chain депозиты
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* Blockchain only toggle */}
                        <button
                            type="button"
                            onClick={() => setBcOnly(v => !v)}
                            className={cn(
                                "inline-flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-[12px] transition",
                                bcOnly
                                    ? "border-emerald-400/40 bg-emerald-500/15 text-emerald-200"
                                    : "border-white/10 bg-white/[0.06] text-white/60 hover:border-white/20 hover:bg-white/10"
                            )}
                        >
                            ⛓ On-chain
                            {bcCount > 0 && (
                                <span className="rounded-full bg-emerald-500/25 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-200">
                                    {bcCount}
                                </span>
                            )}
                        </button>
                        <motion.button
                            whileHover={{ scale: 1.04 }}
                            whileTap={{ scale: 0.97 }}
                            onClick={() => fetchData(page)}
                            disabled={loading}
                            className="inline-flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/[0.06] px-3 py-1.5 text-[12px] text-white/80 transition hover:border-white/20 hover:bg-white/10 disabled:opacity-50"
                        >
                            <ReloadOutlined spin={loading} />
                            Обновить
                        </motion.button>
                    </div>
                </div>
            </section>

            {/* Stats */}
            <section className={cn(shellClass, "shrink-0 px-5 py-3")}>
                <div className="flex flex-wrap items-center gap-4 text-[12px] text-white/50">
                    <div className="flex items-center gap-1.5">
                        <Layers size={13} className="text-white/35" />
                        Всего записей:
                        <span className="font-semibold text-white/80">{total}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <Hash size={13} className="text-emerald-300/60" />
                        On-chain:
                        <span className="font-semibold text-emerald-300/90">{bcCount}</span>
                    </div>
                    {bcOnly && (
                        <span className="rounded-full border border-emerald-400/25 bg-emerald-500/10 px-2 py-0.5 text-[11px] text-emerald-200">
                            Фильтр: только on-chain
                        </span>
                    )}
                    <span className="ml-auto text-white/30">
                        Стр. {page} · {pageSize} на стр.
                    </span>
                </div>
            </section>

            {/* Table */}
            <section className={cn(shellClass, "flex min-h-0 flex-1 flex-col overflow-hidden")}>
                <div className="relative min-h-0 flex-1">
                    {loading && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-[#070A12]/50 backdrop-blur-sm">
                            <Spin size="large" className="[&_.ant-spin-dot-item]:!bg-emerald-400" />
                        </div>
                    )}
                    <div className="h-full min-h-[320px] overflow-auto">
                        <table className="w-full min-w-[960px] border-collapse text-left">
                            <thead className="sticky top-0 z-[1] bg-[#0a0e18]/90 backdrop-blur-md">
                                <tr className="border-b border-white/10">
                                    <th className={th}>
                                        <span className="flex items-center gap-1.5">
                                            <Layers size={11} className="opacity-60" /> Промо
                                        </span>
                                    </th>
                                    <th className={th}>
                                        <span className="flex items-center gap-1.5">
                                            <Coins size={11} className="opacity-60" /> Комиссия
                                        </span>
                                    </th>
                                    <th className={th}>
                                        <span className="flex items-center gap-1.5">
                                            <Hash size={11} className="opacity-60" /> Fee TX
                                        </span>
                                    </th>
                                    <th className={th}>
                                        <span className="flex items-center gap-1.5">
                                            <Hash size={11} className="opacity-60" /> Deposit TX
                                        </span>
                                    </th>
                                    <th className={th}>
                                        <span className="flex items-center gap-1.5">
                                            <Wallet size={11} className="opacity-60" /> Промоутер
                                        </span>
                                    </th>
                                    <th className={th}>Статус ⛓</th>
                                    <th className={th}>
                                        <span className="flex items-center gap-1.5">
                                            <Calendar size={11} className="opacity-60" /> Дата оплаты
                                        </span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <AnimatePresence initial={false}>
                                    {visibleItems.map((item) => (
                                        <motion.tr
                                            key={item.promoId}
                                            initial={{ opacity: 0, y: 4 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0 }}
                                            transition={{ duration: 0.15 }}
                                            className="border-t border-white/[0.06] transition-colors hover:bg-white/[0.025]"
                                        >
                                            {/* Mini promo card */}
                                            <td className={td}>
                                                <PromoMiniCard
                                                    item={item}
                                                    onOpen={handleOpenPromo}
                                                    loading={openingId === item.promoId}
                                                />
                                            </td>

                                            {/* Fee amount */}
                                            <td className={td}>
                                                <div className="flex flex-col gap-1">
                                                    {item.feeAmount != null ? (
                                                        <span className="font-semibold text-emerald-300/90">
                                                            {Number(item.feeAmount).toFixed(2)}
                                                            <span className="ml-1 text-[10px] font-normal text-white/35">
                                                                {item.paymentTokenType || "USDT"}
                                                            </span>
                                                        </span>
                                                    ) : item.feeTxHash ? (
                                                        <span className="text-[11px] text-white/55">on-chain</span>
                                                    ) : (
                                                        <span className="text-white/25">—</span>
                                                    )}
                                                    {(item.isPaid || item.feeTxHash) && (
                                                        <span className="inline-flex w-fit items-center rounded-full border border-emerald-400/30 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] text-emerald-200">
                                                            ✓ Оплачено
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Fee TX */}
                                            <td className={td}>
                                                <HashChip value={item.feeTxHash} />
                                            </td>

                                            {/* Deposit TX */}
                                            <td className={td}>
                                                <div className="flex flex-col gap-1">
                                                    <HashChip value={item.depositTxHash} />
                                                    {item.depositTxHash && item.totalAmount != null && (
                                                        <span className="font-mono text-[11px] text-white/55">
                                                            {Number(item.totalAmount).toLocaleString()}
                                                            <span className="ml-1 text-white/30">deposited</span>
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Promoter address */}
                                            <td className={td}>
                                                {item.promoterAddress ? (
                                                    <Tooltip title={item.promoterAddress}>
                                                        <button
                                                            type="button"
                                                            onClick={() => copyText(item.promoterAddress)}
                                                            className="inline-flex items-center gap-1 font-mono text-[11px] text-white/55 transition hover:text-white/85"
                                                        >
                                                            {short(item.promoterAddress)}
                                                            <Copy size={9} className="opacity-50" />
                                                        </button>
                                                    </Tooltip>
                                                ) : (
                                                    <span className="text-white/20 text-[11px]">—</span>
                                                )}
                                            </td>

                                            {/* Blockchain status */}
                                            <td className={td}>
                                                {item.blockchainStatus ? (
                                                    <Pill
                                                        label={BC_STATUS_LABEL[item.blockchainStatus]?.label || item.blockchainStatus}
                                                        cls={BC_STATUS_LABEL[item.blockchainStatus]?.cls || "border-white/15 bg-white/[0.06] text-white/55"}
                                                    />
                                                ) : (
                                                    <span className="text-white/20 text-[11px]">off-chain</span>
                                                )}
                                            </td>

                                            {/* Date */}
                                            <td className={cn(td, "text-white/35 text-[11px] tabular-nums")}>
                                                {(() => {
                                                    const onChainPaid = !!(item.feeTxHash || item.depositTxHash);
                                                    const dateStr = item.paidAt
                                                        || (onChainPaid ? (item.promoUpdatedAt || item.promoCreatedAt) : null);
                                                    if (!dateStr) return "—";
                                                    const d = new Date(dateStr).toLocaleString("ru-RU", {
                                                        day: "2-digit",
                                                        month: "2-digit",
                                                        year: "2-digit",
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    });
                                                    return !item.paidAt && onChainPaid ? (
                                                        <Tooltip title="Дата последнего on-chain обновления промо">
                                                            <span className="cursor-help">{d}<span className="ml-1 text-white/20">~</span></span>
                                                        </Tooltip>
                                                    ) : d;
                                                })()}
                                            </td>
                                        </motion.tr>
                                    ))}
                                </AnimatePresence>
                            </tbody>
                        </table>

                        {visibleItems.length === 0 && !loading && (
                            <div className="flex flex-col items-center py-20 text-white/35">
                                <ArrowLeftRight className="mb-3 h-10 w-10 opacity-30" />
                                <p className="text-[13px]">Транзакций нет</p>
                                <p className="mt-1 text-[11px] text-white/25">
                                    {bcOnly
                                        ? "Нет on-chain транзакций на этой странице"
                                        : "Транзакции появятся после первой оплаты"}
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Pagination */}
                {total > pageSize && (
                    <div className="shrink-0 border-t border-white/[0.06] px-4 py-3 flex justify-end">
                        <Pagination
                            current={page}
                            pageSize={pageSize}
                            total={total}
                            onChange={(p) => setPage(p)}
                            showSizeChanger={false}
                            className="pagination-custom"
                            size="small"
                        />
                    </div>
                )}
            </section>
        </div>
    );
}
